import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import type { Document } from '@getaccept/lib-shared/src/types/Document';
import { ImportFilesStage } from '../types/enums/import-files-stage';
import { ImportGoogleDriveStage } from '../types/enums/import-google-drive-stage';
import { ImportWordDocumentStage } from '../types/enums/import-word-document-stage';
import type { FileImport } from '../types/file-import';
import type { ImportData } from '../types/import-data';
import type { ImportProgress } from '../types/import-progress';
import { ImportStatus } from '../types/enums/import-status';
import { ImportType } from '../types/enums/import-type';

export class ImporterHelper {
  static getImportingTemplateTitle(type: ImportType, name: string): string {
    let title = t('importing-template').toString();
    if (type === ImportType.GoogleDrive) {
      title = t('saving-google-document').toString();
    }
    if (name) {
      return `${title} "${name}"...`;
    }
    return title;
  }

  static getImportingFilesTitle(files: File[]): string {
    const name: string =
      files.length > 1 ? `${files.length} ${t('files').toString()}` : `"${files[0].name}"`;
    return `${t('uploading').toString()} ${name}...`;
  }

  static getImportingWordDocumentTitle(file: File): string {
    return `${t('uploading').toString()} ${file.name}...`;
  }

  static getImportingGoogleDriveTitle(name: string): string {
    return `${t('importing').toString()} ${name}...`;
  }

  static getTemplateProgress(): ImportProgress {
    return {
      message: t('initializing-import').toString(),
      percentage: 0,
      status: ImportStatus.Importing,
    };
  }

  static getFileProgress(
    data: ImportData,
    importedPageCount: number,
    pageCount: number
  ): ImportProgress {
    const { stage } = data;
    switch (stage) {
      case ImportFilesStage.URLsRecieved:
        return {
          message: t('preparing-to-upload-files').toString(),
          percentage: 10,
          status: ImportStatus.Importing,
        };
      case ImportFilesStage.Uploading:
        return {
          message: t('uploading-files').toString(),
          percentage: 20,
          status: ImportStatus.Importing,
        };
      case ImportFilesStage.Uploaded:
        return {
          message: t('importing-document').toString(),
          percentage: 40,
          status: ImportStatus.Importing,
        };
      case ImportFilesStage.ThumbnailImportStarted:
        return ImporterHelper.getImportingPagesProgress(importedPageCount, pageCount);
      default:
        return {
          message: t('fetching-urls').toString(),
          percentage: 0,
          status: ImportStatus.Importing,
        };
    }
  }

  private static readonly initializePageImport = 'initializing-page-import';

  static getWordDocumentProgress(
    data: ImportData,
    importedPageCount: number,
    pageCount: number
  ): ImportProgress {
    const { stage } = data;
    switch (stage) {
      case ImportWordDocumentStage.Uploaded:
        return {
          message: t('importing-document').toString(),
          percentage: 10,
          status: ImportStatus.Importing,
        };
      case ImportWordDocumentStage.Processed:
        return {
          message: t('saving-log-file').toString(),
          percentage: 20,
          status: ImportStatus.Importing,
        };
      case ImportWordDocumentStage.LogSaved:
        return {
          message: t(ImporterHelper.initializePageImport).toString(),
          percentage: 40,
          status: ImportStatus.Importing,
        };
      case ImportFilesStage.ThumbnailImportStarted:
        return ImporterHelper.getImportingPagesProgress(importedPageCount, pageCount);
      default:
        return {
          message: t('uploading-word-document').toString(),
          percentage: 0,
          status: ImportStatus.Importing,
        };
    }
  }

  static getGoogleDocsProgress(
    data: ImportData,
    importedPageCount: number,
    importedDocument: Document,
    pageCount: number
  ): ImportProgress {
    if (importedDocument && !data.stage) {
      return {
        message: t(ImporterHelper.initializePageImport).toString(),
        percentage: 30,
        status: ImportStatus.Importing,
      };
    } else if (data.stage === ImportFilesStage.ThumbnailImportStarted) {
      return ImporterHelper.getImportingPagesProgress(importedPageCount, pageCount);
    }
    return {
      message: t('importing-template').toString(),
      percentage: 0,
      status: ImportStatus.Importing,
    };
  }

  static getGoogleDriveProgress(
    data: ImportData,
    importedPageCount: number,
    pageCount: number
  ): ImportProgress {
    const { stage } = data;
    switch (stage) {
      case ImportGoogleDriveStage.Uploaded:
        return {
          message: t('saving').toString(),
          percentage: 10,
          status: ImportStatus.Importing,
        };
      case ImportGoogleDriveStage.Saved:
        return {
          message: t('saving-log-file').toString(),
          percentage: 20,
          status: ImportStatus.Importing,
        };
      case ImportGoogleDriveStage.LogSaved:
        return {
          message: t(ImporterHelper.initializePageImport).toString(),
          percentage: 40,
          status: ImportStatus.Importing,
        };
      case ImportFilesStage.ThumbnailImportStarted:
        return ImporterHelper.getImportingPagesProgress(importedPageCount, pageCount);
      default:
        return {
          message: t('uploading').toString(),
          percentage: 0,
          status: ImportStatus.Importing,
        };
    }
  }

  static getErrorProgress(type: ImportType, error: string): ImportProgress {
    let message = '';
    switch (type) {
      case ImportType.Template:
        message = t('failed-to-use-template').toString();
        break;
      case ImportType.GoogleDocs:
      case ImportType.GoogleDrive:
        message = t('failed-to-use-google-document').toString();
        break;
      case ImportType.File:
        message = t('failed-to-import-files').toString();
        break;
      case ImportType.WordDocument:
        message = t('failed-to-import-word-document').toString();
        break;
      default:
        message = t('import-failed').toString();
    }
    return {
      message: `${message} ${t('error').toString()}: "${error}"`,
      percentage: 100,
      status: ImportStatus.Error,
    };
  }

  static pageImportDone(fileImport: FileImport, importedPageCount: number): boolean {
    const actualFileCount: number = fileImport?.recievedFileImportCount;
    const expectedFileCount: number = fileImport?.fileCount;
    if (expectedFileCount !== actualFileCount) {
      return false;
    }
    const expectedPageCount: number = fileImport?.pageCount;
    const actualPageCount: number = importedPageCount;
    return expectedPageCount === actualPageCount;
  }

  static pagePercentage(total: number, current: number, max = 25): number {
    return (max / total) * current;
  }

  private static getImportingPagesProgress(
    importedPageCount: number,
    totalFileCount: number
  ): ImportProgress {
    if (!importedPageCount) {
      return {
        message: t('preparing-import').toString(),
        percentage: 50,
        status: ImportStatus.Importing,
      };
    }
    return {
      message: `${t('importing-pages').toString()} (${importedPageCount}/${totalFileCount})`,
      percentage: 50 + ImporterHelper.pagePercentage(totalFileCount, importedPageCount, 50),
      status: ImportStatus.Importing,
    };
  }
}
